import { useMemo, useState } from 'react';
import { useAuthStore } from '../auth/useAuthStore';
import { usePortalAccess } from './PortalAccessContext';
import { SystemMapHeader, SystemMapMapWrap, SystemMapSidePanel } from './SystemMapPageSections';
import {
  normalizeStatus,
  useCanvasAreaProps,
  useDerivedMapProps,
  useEditMode,
  useMapSelection,
  useNewStopComputed,
  useReviewAutoSelect,
  useStatusFilter,
  useSystemMapData,
  useSystemMapPageActions,
  useUrbanNetwork,
} from './systemMapPageHooks';
import { useNewStopDraft, useReviewWorkflow, useSessionOverrides } from './systemMapReview';
import './systemMap.css';

/**
 * Mapa del sistema — vista complementaria de `/analysis`. Fase 1: 21 rutas
 * cerradas + ~1735 `TransitStop` con estado real, READ ONLY absoluto.
 * Fase B (esta capa): revisión secuencial de `needs_review`, edición de
 * asociación y alta de paradas — sin ninguna escritura real: todo vive en
 * memoria de React y se pierde al recargar la página (ver auditoría Fase A,
 * `SM13`, que encontró que hoy no hay datasource de escritura seguro).
 */
export function SystemMapPage(): JSX.Element {
  const { slug } = usePortalAccess();
  const { token } = useAuthStore();
  const { routes, stops, loadState } = useSystemMapData(slug, token);

  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const { activeStatuses, toggleStatus, resetStatuses } = useStatusFilter();
  const [urbanNetworkEnabled, setUrbanNetworkEnabled] = useState(false);
  const { edges: urbanNetworkEdges, loading: urbanNetworkLoading } = useUrbanNetwork(
    slug,
    token,
    urbanNetworkEnabled
  );
  const selection = useMapSelection(slug, token, urbanNetworkEdges);
  const editMode = useEditMode(selection.selectedStopId);
  const sessionOverrides = useSessionOverrides();
  const newStopDraft = useNewStopDraft();
  const draftComputed = useNewStopComputed(newStopDraft.draft, urbanNetworkEdges, stops);

  const needsReviewStopIds = useMemo(
    () => (stops ? stops.stops.filter(s => s.status === 'needs_review').map(s => s.id) : []),
    [stops]
  );
  const review = useReviewWorkflow(needsReviewStopIds);
  useReviewAutoSelect(review.currentStopId, selection.selectStop);

  const visibleStops = useMemo(() => {
    if (!stops) return [];
    return stops.stops.filter(s => activeStatuses.has(normalizeStatus(s.status)));
  }, [stops, activeStatuses]);
  const sessionOverrideIds = useMemo(
    () => Array.from(sessionOverrides.overrides.keys()),
    [sessionOverrides.overrides]
  );
  const derived = useDerivedMapProps(newStopDraft, draftComputed, selection.selectedStopId, sessionOverrides, stops);

  const actions = useSystemMapPageActions({
    selection,
    editMode,
    sessionOverrides,
    newStopDraft,
    urbanNetworkEdges,
    setUrbanNetworkEnabled,
    setSelectedLineId,
    resetStatuses,
  });
  const canvasAreaProps = useCanvasAreaProps({
    routes,
    visibleStops,
    selectedLineId,
    setSelectedLineId,
    activeStatuses,
    toggleStatus,
    actions,
    urbanNetworkEnabled,
    urbanNetworkEdges,
    urbanNetworkLoading,
    setUrbanNetworkEnabled,
    selection,
    review,
    newStopDraft,
    derived,
    sessionOverrideIds,
  });

  return (
    <div className="smap-page">
      <SystemMapHeader stopsTotalLabel={derived.stopsTotalLabel} />

      <div className="smap-body">
        <SystemMapMapWrap
          loadState={loadState}
          stops={stops}
          canvasAreaProps={canvasAreaProps}
          review={review}
          stopName={selection.detail?.name ?? null}
          confirmed={derived.confirmed}
          editing={editMode.editing}
          onConfirm={actions.handleConfirmInSession}
          onEditAssociation={actions.handleToggleEdit}
        />

        <SystemMapSidePanel
          draft={newStopDraft.draft}
          draftComputed={draftComputed}
          onUpdateDraft={newStopDraft.updateDraft}
          onClearManualOverride={() => newStopDraft.updateDraft({ manualEdgeId: null })}
          onCancelDraft={() => newStopDraft.clearDraft()}
          selection={selection}
          editing={editMode.editing}
          sessionOverride={derived.sessionOverride}
          onToggleEdit={actions.handleToggleEdit}
          onCancelEdit={actions.handleCancelEdit}
          onConfirmInSession={actions.handleConfirmInSession}
        />
      </div>
    </div>
  );
}
